import { Menu, MenuButton, MenuList, MenuItem, MenuDivider, Box, HStack } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { Tag } from 'Atoms';
import { ChevronDownIcon } from 'Tokens/Icons/Direction';
import { LockedIcon } from 'Tokens/Icons/Function';
import { MetricMaterialityState } from '../../DoubleMaterialityAssessment/DoubleMaterialityAssessment.d';
import {
  BOX_STATES,
  VSME_BOX_TEXT_STATES,
} from '../../DoubleMaterialityAssessment/MaterialityUtils';
import { BaseMetric } from './types';
import { Typography } from 'Tokens';
import { useParams } from 'react-router-dom';
import { FrameworkRefs } from 'containers/Esrs/pieces/Assessment';

interface MaterialityMenuCellProps<T extends BaseMetric> {
  metric: T;
  materiality: MetricMaterialityState;
  isLocked: boolean;
  metricFramework: string;
  selectOptions: Array<{ label: string; value: MetricMaterialityState }>;
  onUpdateMateriality: (data: {
    selection: MetricMaterialityState;
    metric: T;
    materialStandardId?: string;
    isChild?: boolean;
  }) => void;
  onDeleteMetric: (metric: T) => void;
  materialStandardId?: string;
  isDropDownDisabled?: boolean;
  disableNotIncluded?: boolean;
}

export const MaterialityMenuCell = <T extends BaseMetric>({
  metric,
  materiality,
  isLocked,
  metricFramework,
  selectOptions,
  onUpdateMateriality,
  onDeleteMetric,
  materialStandardId,
  isDropDownDisabled = false,
  disableNotIncluded = false,
}: MaterialityMenuCellProps<T>) => {
  const { framework = '' } = useParams();
  const isNotIncluded =
    materiality === MetricMaterialityState.toAssess ||
    materiality === MetricMaterialityState.doNotInclude;

  const title =
    framework === FrameworkRefs.vsme && VSME_BOX_TEXT_STATES[materiality as MetricMaterialityState]
      ? VSME_BOX_TEXT_STATES[materiality]
      : BOX_STATES[materiality].text;

  return (
    <Menu placement="bottom-end" size="lg">
      <MenuButton disabled={isDropDownDisabled}>
        <Tag
          title={title}
          rightIcon={
            isLocked ? (
              <LockedIcon color="text.selected" />
            ) : (
              <ChevronDownIcon
                color={isDropDownDisabled ? 'text.muted' : BOX_STATES[materiality].textColor}
              />
            )
          }
          truncatable
          size="xs"
          width={isLocked ? '100px' : BOX_STATES[materiality].width}
          border="none"
          clickable={!isLocked}
          bgColor={isDropDownDisabled ? 'bg.unknown' : BOX_STATES[materiality].bgColor}
          color={isDropDownDisabled ? 'text.muted' : BOX_STATES[materiality].textColor}
        />
      </MenuButton>
      {!isLocked && (
        <MenuList minW="200px">
          {framework !== metricFramework && !disableNotIncluded && (
            <>
              <Box padding="8px">
                <Typography variant="detail" p="8px">
                  This data point is optional for your assessment
                </Typography>
                <MenuItem>
                  <HStack
                    justify="space-between"
                    align="center"
                    w="100%"
                    borderRadius="md"
                    cursor="pointer"
                    onClick={() => onDeleteMetric(metric)}
                  >
                    <Typography variant="bodyStrong" color={isNotIncluded ? 'text.selected' : ''}>
                      Not included
                    </Typography>
                    {isNotIncluded && <CheckIcon color="text.selected" boxSize={3} />}
                  </HStack>
                </MenuItem>
              </Box>
              <MenuDivider />
            </>
          )}
          <Box padding="8px">
            {selectOptions.map((opt) => (
              <MenuItem key={opt.value}>
                <HStack
                  justify="space-between"
                  align="center"
                  w="100%"
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => {
                    if (opt?.value !== materiality) {
                      onUpdateMateriality({
                        selection: opt?.value ?? MetricMaterialityState.toAssess,
                        metric,
                        materialStandardId,
                      });
                    }
                  }}
                >
                  <Typography
                    variant="bodyStrong"
                    color={opt.value === materiality ? 'text.selected' : ''}
                  >
                    {opt.label}
                  </Typography>
                  {opt.value === materiality && <CheckIcon color="text.selected" boxSize={3} />}
                </HStack>
              </MenuItem>
            ))}
          </Box>
        </MenuList>
      )}
    </Menu>
  );
};
